import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Gift } from "lucide-react";

export default function SetPrizeModal({ childId, childName, onClose, t }) {
  const [prizeName, setPrizeName] = useState("");
  const [prizeDescription, setPrizeDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPrize, setCurrentPrize] = useState(null);
  const [loadingPrize, setLoadingPrize] = useState(true);

  useEffect(() => {
    loadCurrentPrize();
  }, [childId]);

  const loadCurrentPrize = async () => {
    try {
      const { data } = await axios.get(`${API}/children/${childId}/weekly-prize`);
      if (data.prize) {
        setCurrentPrize(data.prize);
        setPrizeName(data.prize.prize_name);
        setPrizeDescription(data.prize.prize_description || "");
      }
    } catch (e) { console.error(e); }
    finally { setLoadingPrize(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prizeName.trim()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/children/${childId}/weekly-prize`, {
        prize_name: prizeName.trim(),
        prize_description: prizeDescription.trim()
      });
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || t('could_not_save_prize'));
    } finally {
      setLoading(false);
    }
  };

  const PRIZE_SUGGESTIONS = [
    t('prize_ice_cream'),
    t('prize_screen_time'),
    t('prize_pick_dinner'),
    t('prize_movie_night'),
    t('prize_stay_up_late'),
    t('prize_new_book'),
    t('prize_park_day'),
    t('prize_pizza_party')
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
        <div className="text-center mb-6">
          <div className="bg-gradient-to-br from-[#FFD166] to-[#F77F00] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <Gift size={32} color="white" />
          </div>
          <h2 className="text-2xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
            {t('weekly_prize_for')} {childName}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {t('set_reward_desc')}
          </p>
        </div>

        {loadingPrize ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-[#FFD166] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('prize_name')} *</label>
              <input
                type="text"
                value={prizeName}
                onChange={(e) => setPrizeName(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#FFD166] transition-colors text-gray-800 font-medium"
                placeholder={t('prize_placeholder')}
                required
                maxLength={50}
                data-testid="prize-name-input"
              />
            </div>
            
            {/* Quick suggestions */}
            <div className="flex flex-wrap gap-2">
              {PRIZE_SUGGESTIONS.map((suggestion, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPrizeName(suggestion)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${
                    prizeName === suggestion 
                      ? 'bg-[#FFD166] text-white' 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('description_optional')}</label>
              <input
                type="text"
                value={prizeDescription}
                onChange={(e) => setPrizeDescription(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#FFD166] transition-colors text-gray-800 font-medium"
                placeholder={t('prize_desc_placeholder')}
                maxLength={100}
                data-testid="prize-description-input"
              />
            </div>

            {currentPrize && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-600 font-bold">{t('current_prize_label')}:</p>
                <p className="text-amber-800 font-bold">{currentPrize.prize_name}</p>
                {currentPrize.prize_description && (
                  <p className="text-amber-600 text-sm">{currentPrize.prize_description}</p>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition-all"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || !prizeName.trim()}
                className="flex-1 bg-gradient-to-r from-[#FFD166] to-[#F77F00] hover:opacity-90 text-white font-black py-3 rounded-xl transition-all disabled:opacity-60"
                style={{ fontFamily: "Fredoka, sans-serif" }}
                data-testid="save-prize-btn"
              >
                {loading ? t('saving') : currentPrize ? t('update_prize') : t('set_prize')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
